import { NextRouter } from 'next/router';

const initPath = '/[ssgParams]';

const getPathName = (pathname: string) => {
  if (pathname === initPath) return '/';
  if (pathname.includes(initPath)) return `${pathname}`.split(initPath)?.[1];

  return pathname;
};

const getQuery = (query: Record<string, any>) => {
  const params = { ...query };
  if (params?.ssgParams) delete params.ssgParams;
  return params;
};

const updateQueryParam = (router: NextRouter, paramName: string, paramValue: string) => {
  const query = getQuery(router.query);

  const newQuery = { ...query, [paramName]: paramValue };
  router.push({ pathname: getPathName(router.pathname), query: newQuery }, undefined, { scroll: false, shallow: true });
};

const updateQueryParams = (router: NextRouter, params: Record<string, string | null>) => {
  const query = getQuery(router.query);

  const toRemove = Object.keys(query).filter((key) => !params[key]);

  const newQuery = Object.entries({ ...query, ...params })
    .filter(([key]) => !toRemove.includes(key))
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  router.push({ pathname: getPathName(router.pathname), query: newQuery });
};

const setQuery = ({
  router,
  val,
  queryName,
  deleteQuery,
}: {
  router: NextRouter;
  val?: string;
  queryName: string;
  deleteQuery?: string;
}) => {
  const params = getQuery(router.query);

  let page = {};
  if (router.query?.page) {
    page = { page: router.query?.page };
  }

  const query: Record<string, any> = deleteQuery
    ? {
        ...Object.entries(params)
          .filter(([key]) => key !== deleteQuery)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
        ...page,
      }
    : { ...params, ...page };

  if (val) query[queryName] = val;

  router.push({
    pathname: getPathName(router.pathname),
    query: val
      ? query
      : Object.entries(query)
          .filter(([key]) => key !== queryName)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
  });
};

const deleteQuery = (router: NextRouter, urlQuery: string) => {
  const params = getQuery(router.query);

  return router.replace(
    {
      pathname: getPathName(router.pathname),
      query: Object.entries(params)
        .filter(([key]) => key !== urlQuery)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
    },
    undefined,
    {
      scroll: false,
    },
  );
};

const deleteManyQuery = (router: NextRouter, queries: string[]) => {
  const params = getQuery(router.query);

  return router.replace(
    {
      pathname: getPathName(router.pathname),
      query: Object.entries(params)
        .filter(([key]) => !queries.includes(key))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
    },
    undefined,
    {
      scroll: false,
    },
  );
};

const routerPush = (router: NextRouter, href: string, as: string, options: any) =>
  router.push(href, as, options);
const routerReplace = (router: NextRouter, href: string) => router.replace(href);
const routerReload = (router: NextRouter) => router.reload();

export {
  deleteQuery,
  deleteManyQuery,
  setQuery,
  getPathName,
  getQuery,
  updateQueryParam,
  updateQueryParams,
  routerPush,
  routerReplace,
  routerReload,
};
